import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { RoutingPage, setPage } from '../../routes/routerSlice';
import { ActionsBar, MainContentWrapper, Section } from '@ydistri/ds';
import CloseAllStatisticsAction from './actions/CloseAllStatisticsAction';
import {
  filterCalculations,
  initialCalculationTableFilters,
  resetExpandedRowKeys,
} from './calculationsSlice';
import CurrentUserCalculationsAction from './actions/CurrentUserCalculationsAction';
import ProductionCalculationsAction from './actions/ProductionCalculationsAction';
import CalculationsTable from './components/table/CalculationsTable';
import { useUser } from '../../hooks/useUser';
import { createDebugLog } from '@ydistri/utils';
import SelectedCalculationsActionButtons from './actions/SelectedCalculationsActionButtons';
import { getIconBasedOnPrivateFilterType, PrivateFilterType } from './calculationsTypes';
import { ItemType } from 'antd/es/menu/interface';
import { Dropdown } from 'antd';
import ToggleIconButton from '../../components/buttons/ToggleIconButton';
import CreateCalculationButton from '../Configuration/CreateCalculationButton';
import { CalculationType, CalculationWithOverviewResponse } from '@ydistri/api-sdk';
import { isCalculationInTemporaryStatus } from './calculationsLib';
import { useAppDispatch } from '../../store';
import { useSelectedProjectShortName } from '../../hooks/useSelectedProjectShortName';
import { getItem, LSKey } from '../../store/localStore';
import CalculationsHeader from './components/header/CalculationsHeader';

const debugLog = createDebugLog('Calculations', 'Calculations');

const baseCalculationsTableId = 'baseTable';

const Calculations: React.FC = () => {
  debugLog('rendering');
  const dispatch = useAppDispatch();
  const user = useUser();
  const projectShortName = useSelectedProjectShortName();

  const calculationFilters = useSelector(state => state.calculations.calculationFilters);

  useEffect(() => {
    dispatch(setPage(RoutingPage.CALCULATIONS));
  }, [dispatch]);

  useEffect(() => {
    if (!projectShortName) return;
    const filtersInLocalStore = getItem(LSKey.CALCULATION_TABLE_FILTERS)?.[projectShortName];
    if (filtersInLocalStore) {
      dispatch(
        filterCalculations({
          projectShortName,
          data: {
            ...initialCalculationTableFilters,
            ...filtersInLocalStore,
          },
        }),
      );
    }
  }, [projectShortName, dispatch]);

  const setPrivateCalculationFilter = useCallback(
    (value: PrivateFilterType) => {
      if (!projectShortName) return;
      dispatch(
        filterCalculations({
          projectShortName,
          data: {
            showAllPrivateCalculations: value,
            showCurrentUserCalculationsOnly: false,
            showProductionOnly: false,
          },
        }),
      );
    },
    [projectShortName, dispatch],
  );

  const toggleShowCurrentUserCalculations = useCallback(() => {
    if (!projectShortName) return;
    dispatch(
      filterCalculations({
        projectShortName,
        data: {
          showCurrentUserCalculationsOnly: !calculationFilters.showCurrentUserCalculationsOnly,
          showAllPrivateCalculations: calculationFilters.showAllPrivateCalculations,
          showProductionOnly: calculationFilters.showProductionOnly,
        },
      }),
    );
  }, [projectShortName, calculationFilters, dispatch]);

  const toggleShowProductionCalculations = useCallback(() => {
    if (!projectShortName) return;
    dispatch(
      filterCalculations({
        projectShortName,
        data: {
          ...calculationFilters,
          showProductionOnly: !calculationFilters.showProductionOnly,
        },
      }),
    );
  }, [projectShortName, calculationFilters, dispatch]);

  const closeAllStatistics = useCallback(() => {
    dispatch(resetExpandedRowKeys('all'));
  }, [dispatch]);

  const menuItemClickHandler = useCallback(
    (menuInfo: {
      key: string;
      domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
    }) => {
      const { key, domEvent } = menuInfo;

      domEvent.preventDefault();

      switch (key) {
        case PrivateFilterType.ALL.toString():
          setPrivateCalculationFilter(PrivateFilterType.ALL);
          break;
        case PrivateFilterType.MINE.toString():
          setPrivateCalculationFilter(PrivateFilterType.MINE);
          break;
        case PrivateFilterType.NONE.toString():
          setPrivateCalculationFilter(PrivateFilterType.NONE);
          break;
        default:
          return;
      }
    },
    [setPrivateCalculationFilter],
  );

  const menuItems = useMemo(() => {
    const items: ItemType[] = [
      {
        label: 'Show all private calculations',
        key: PrivateFilterType.ALL,
        icon: getIconBasedOnPrivateFilterType(PrivateFilterType.ALL),
        onClick: menuItemClickHandler,
      },
      {
        label: 'Show only my private calculations',
        key: PrivateFilterType.MINE,
        icon: getIconBasedOnPrivateFilterType(PrivateFilterType.MINE),
        onClick: menuItemClickHandler,
      },
      {
        label: 'Hide private calculations',
        key: PrivateFilterType.NONE,
        icon: getIconBasedOnPrivateFilterType(PrivateFilterType.NONE),
        onClick: menuItemClickHandler,
      },
    ];

    return {
      items,
      selectable: true,
      selectedKeys: [calculationFilters.showAllPrivateCalculations],
    };
  }, [menuItemClickHandler, calculationFilters.showAllPrivateCalculations]);

  const headerActions = useMemo(() => {
    return (
      <ActionsBar>
        <SelectedCalculationsActionButtons calculationsTableId={baseCalculationsTableId} />
        {user?.isSuperUser && (
          <Dropdown menu={menuItems}>
            <ToggleIconButton
              size="small"
              checked={calculationFilters.showAllPrivateCalculations !== PrivateFilterType.NONE}
              icon={getIconBasedOnPrivateFilterType(
                calculationFilters.showAllPrivateCalculations,
                'small',
              )}
            />
          </Dropdown>
        )}
        <CurrentUserCalculationsAction
          onClick={toggleShowCurrentUserCalculations}
          tooltip={
            calculationFilters.showCurrentUserCalculationsOnly
              ? "Show also other people's calculations"
              : 'Show my calculations only'
          }
          value={calculationFilters.showCurrentUserCalculationsOnly}
          size="small"
        />
        <ProductionCalculationsAction
          tooltip={
            calculationFilters.showProductionOnly
              ? 'Show all calculations'
              : 'Show production calculations only'
          }
          value={calculationFilters.showProductionOnly}
          onClick={toggleShowProductionCalculations}
          size="small"
        />
        <CloseAllStatisticsAction
          onClick={closeAllStatistics}
          size="small"
          calculationTableId={baseCalculationsTableId}
        />
        <CreateCalculationButton disabled={false} displayTemplateSelect={true} />
      </ActionsBar>
    );
  }, [
    menuItems,
    calculationFilters,
    closeAllStatistics,
    toggleShowCurrentUserCalculations,
    toggleShowProductionCalculations,
    user,
  ]);

  const selectableRules = useCallback(
    (c: CalculationWithOverviewResponse) =>
      c.type === CalculationType.Production || isCalculationInTemporaryStatus(c.status),
    [],
  );

  return (
    <MainContentWrapper>
      <Helmet title="Calculations" />
      <Section
        header={<CalculationsHeader />}
        data-id="CalculationsSection"
        headerActions={headerActions}
      >
        <CalculationsTable
          calculationsTableId={baseCalculationsTableId}
          selectableRules={selectableRules}
        />
      </Section>
    </MainContentWrapper>
  );
};

export default Calculations;
